import PropTypes from "prop-types";
// @mui
import Box from "@mui/material/Box";

import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import { avatars } from "@/utils/profileMetaData";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { observer } from "mobx-react";
import { fToNow } from "@/utils/format-time";
import { formatDate } from "@/utils/format-time";

// ----------------------------------------------------------------------

const NotificationItem = observer(
  ({ notification, markAsRead, careerClimbs, sections }) => {
    const currentUser = useCurrentUser();

    const assignmentDetails = careerClimbs.find(
      (climb) => climb.learnosityTag === notification.learnosityTag,
    );
    let userAvatar = null;
    if (notification.type === "careerClimbCompleted") {
      userAvatar = avatars.find(
        (avatar) => avatar.id === parseInt(notification.image),
      )
        ? avatars.find((avatar) => avatar.id === parseInt(notification.image))
            .src
        : avatars[0].src;
    }

    const renderImage = (
      <ListItemAvatar>
        {userAvatar ? (
          <Avatar src={userAvatar} sx={{ bgcolor: "background.neutral" }} />
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "background.neutral",
            }}
          >
            <Box
              component="img"
              src={notification.image}
              sx={{ width: 40, height: 40, borderRadius: "8px" }}
            />
          </Stack>
        )}
      </ListItemAvatar>
    );

    const renderDueByText = (
      <ListItemText
        disableTypography
        primary={reader(notification.content)}
        secondary={
          <Stack
            direction="row"
            alignItems="center"
            sx={{ typography: "caption", color: "#078DEE" }}
            divider={
              <Box
                sx={{
                  width: 2,
                  height: 2,
                  bgcolor: "currentColor",
                  mx: 0.5,
                  borderRadius: "50%",
                }}
              />
            }
          >
            {notification.type === "careerClimbAssigned" &&
              assignmentDetails?.dueBy && (
                <>Due: {formatDate(assignmentDetails.dueBy)}</>
              )}
            {/* {fToNow(climb.dueBy)} */}
            {/* {notification.category} */}
          </Stack>
        }
      />
    );

    const renderMessageSentDate = (
      <Stack
        direction="row"
        alignItems="center"
        sx={{ typography: "caption", color: "text.disabled" }}
        divider={
          <Box
            sx={{
              width: 2,
              height: 2,
              bgcolor: "currentColor",
              mx: 0.5,
              borderRadius: "50%",
            }}
          />
        }
      >
        {fToNow(notification.timestamp)}
        {sections.find((section) => section.id === notification.parentID)
          ?.sectionName || ""}
        {notification.type === "careerClimbAssigned" &&
          notification.sender &&
          `${notification.sender.firstName.charAt(0).toUpperCase()}. ${notification.sender.lastName}`}
        {/* {profileStore.getSectionById(notification.parentID).sectionName} */}
      </Stack>
    );

    const renderUnReadBadge = () => {
      const isRead = notification.readBy.includes(currentUser.userID);
      if (!isRead) {
        return (
          <Box
            sx={{
              top: 26,
              width: 8,
              height: 8,
              right: 20,
              borderRadius: "50%",
              bgcolor: "info.main",
              position: "absolute",
            }}
          />
        );
      }
    };

    return (
      <ListItemButton
        disableRipple
        sx={{
          p: 2.5,
          alignItems: "flex-start",
          mr: 2,
          borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
        }}
        onClick={() => {
          markAsRead(notification, assignmentDetails);
        }}
      >
        {renderUnReadBadge()}

        {renderImage}

        <Stack sx={{ flexGrow: 1 }}>
          {renderDueByText}
          {renderMessageSentDate}
        </Stack>
      </ListItemButton>
    );
  },
);

NotificationItem.propTypes = {
  notification: PropTypes.object,
  markAsRead: PropTypes.func,
};

// ----------------------------------------------------------------------

function reader(data) {
  return (
    <Box
      dangerouslySetInnerHTML={{ __html: data }}
      sx={{
        mb: 0.5,
        "& p": { typography: "body2", m: 0 },
        "& a": { color: "inherit", textDecoration: "none" },
        "& strong": { typography: "subtitle2" },
        "& b": { typography: "subtitle2", fontFamily: "ProximaNova Bold" },
      }}
    />
  );
}

export default NotificationItem;
